import { useCallback, useEffect, useMemo, useState } from 'react';

export function emptyNavAlerts(countKeys) {
  return Object.fromEntries(countKeys.map(key => [key, 0]));
}

/** Parse a client-only visual QA override, rejecting malformed values as a whole. */
export function parseNavAlertPreview(search, previewParam, countKeys) {
  const value = new URLSearchParams(search || '').get(previewParam);
  if (!value) return null;

  const alerts = emptyNavAlerts(countKeys);
  const seen = new Set();
  for (const entry of value.split(',')) {
    const [key, countText, ...extra] = entry.split(':');
    const count = Number(countText);
    if (
      extra.length
      || !countKeys.includes(key)
      || seen.has(key)
      || !/^\d+$/.test(countText || '')
      || !Number.isSafeInteger(count)
    ) {
      return null;
    }
    alerts[key] = count;
    seen.add(key);
  }

  return seen.size ? alerts : null;
}

export function normalizeNavCounts(counts, countKeys) {
  return Object.fromEntries(countKeys.map(key => {
    const count = Number(counts?.[key]);
    return [key, Number.isSafeInteger(count) && count >= 0 ? count : 0];
  }));
}

/** Compare totals to a local baseline without marking the initial load as new activity. */
export function compareNavCounts(counts, baseline, countKeys) {
  const normalizedCounts = normalizeNavCounts(counts, countKeys);
  const nextBaseline = {};
  const alerts = {};

  for (const key of countKeys) {
    const previous = Number(baseline?.[key]);
    if (!Number.isSafeInteger(previous) || previous < 0) {
      nextBaseline[key] = normalizedCounts[key];
      alerts[key] = 0;
      continue;
    }

    alerts[key] = Math.max(0, normalizedCounts[key] - previous);
    nextBaseline[key] = normalizedCounts[key] < previous ? normalizedCounts[key] : previous;
  }

  return { counts: normalizedCounts, baseline: nextBaseline, alerts };
}

function readBaseline(storageKey) {
  if (!storageKey || typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeBaseline(storageKey, baseline) {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(baseline));
  } catch {
    // Storage can be unavailable in privacy modes. Counts still render without persistence.
  }
}

/**
 * Fetch and compare browser-local activity hints. The caller owns identity, endpoint, and storage
 * namespace; this hook intentionally knows nothing about seller tokens or shopper sessions.
 */
export default function useNavAlerts({
  fetch: request,
  apiBase,
  endpoint,
  storageKey,
  previewParam,
  countKeys,
  itemCountKeys,
  activeItemId,
}) {
  const [counts, setCounts] = useState(null);
  const [alerts, setAlerts] = useState(() => emptyNavAlerts(countKeys));
  const previewSearch = typeof window === 'undefined' ? '' : window.location.search;
  const previewAlerts = useMemo(
    () => parseNavAlertPreview(previewSearch, previewParam, countKeys),
    [countKeys, previewParam, previewSearch],
  );

  useEffect(() => {
    if (!storageKey) return undefined;

    let canceled = false;
    setCounts(null);
    if (previewAlerts) {
      setCounts(previewAlerts);
      setAlerts(previewAlerts);
      return undefined;
    }
    setAlerts(emptyNavAlerts(countKeys));

    const loadCounts = async () => {
      try {
        const response = await request(`${apiBase}${endpoint}`);
        const body = await response.json();
        if (!response.ok) {
          throw new Error(body.message || body.error || 'Failed to load sidebar counts');
        }
        if (canceled) return;

        const compared = compareNavCounts(body.data, readBaseline(storageKey), countKeys);
        writeBaseline(storageKey, compared.baseline);
        setCounts(compared.counts);
        setAlerts(compared.alerts);
      } catch {
        // Navigation remains usable when optional activity counts cannot load.
      }
    };

    loadCounts();
    return () => {
      canceled = true;
    };
  }, [apiBase, countKeys, endpoint, previewAlerts, request, storageKey]);

  const acknowledge = useCallback(itemId => {
    const countKey = itemCountKeys[itemId];
    if (previewAlerts || !countKey || !counts || !storageKey) return;

    writeBaseline(storageKey, {
      ...readBaseline(storageKey),
      [countKey]: counts[countKey],
    });
    setAlerts(previous => ({ ...previous, [countKey]: 0 }));
  }, [counts, itemCountKeys, previewAlerts, storageKey]);

  useEffect(() => {
    acknowledge(activeItemId);
  }, [acknowledge, activeItemId]);

  return { counts, alerts, acknowledge };
}
